const PENDING_ROUTE_KEY = "hhn.auth.pendingRoute";

/**
 * Web sign-in leaves the app entirely: Amplify's signInWithRedirect hands the
 * browser to the Cognito hosted UI, which returns to the fixed
 * REACT_APP_COGNITO_REDIRECT_SIGN_IN origin — never the URL the user asked for.
 * Router state and a ?returnTo query both die in that round trip, so a deep
 * link has to survive outside the URL.
 *
 * sessionStorage, not localStorage: the destination belongs to the tab that was
 * sent to log in, and it must not outlive the session.
 */
export function storePendingRoute(route: string): void {
  try {
    sessionStorage.setItem(PENDING_ROUTE_KEY, route);
  } catch {
    // Private mode or quota: login still works, it just lands on the default
    // post-login route instead of the deep link.
  }
}

/** Reads the route and removes it, so a login is redirected exactly once. */
export function consumePendingRoute(): string | null {
  try {
    const route = sessionStorage.getItem(PENDING_ROUTE_KEY);
    sessionStorage.removeItem(PENDING_ROUTE_KEY);
    return isInternalRoute(route) ? route : null;
  } catch {
    return null;
  }
}

export function clearPendingRoute(): void {
  try {
    sessionStorage.removeItem(PENDING_ROUTE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}

/**
 * Only ever navigate to a path inside this app. "//evil.com" and
 * "https://evil.com" are both valid <Navigate to> targets, so a route that
 * reached storage through anything other than storePendingRoute must not be
 * able to send the user off-origin.
 */
function isInternalRoute(route: string | null): route is string {
  return !!route && route.startsWith("/") && !route.startsWith("//");
}
